// ============================================================================
// COLOR SCHEMES CONFIGURATION
// ============================================================================
// This file contains color schemes for multiple wedding events.
// Currently, only RECEPTION_COLORS is active in the application.
// SANGEET_COLORS is kept for reference and will be used when cloning this
// repo for a separate Sangeet Night domain/subdomain.
// ============================================================================

// Color scheme type definition
export interface ColorScheme {
  // Primary: Main background colors
  primary: string;
  primaryHover: string;
  primaryLight: string;
  
  // Secondary: Text and accent backgrounds
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;
  
  // Accent: Highlight and decorative elements
  accent: string;
  accentHover: string;
  accentLight: string;
  
  // Additional colors
  text: string;
  textLight: string;
  border: string;
  borderLight: string;
  
  // Status colors
  error: string;
  success: string;
  
  // Background gradients
  gradientStart: string;
  gradientEnd: string;
  
  // Overlay and shadows
  overlay: string;
  shadow: string;
}

// Reception Color Scheme: Elegant and formal (navy blue & gold)
export const RECEPTION_COLORS: ColorScheme = {
  // Primary: Soft ivory/cream background
  primary: '#F5F0E8',
  primaryHover: '#E8E0D5',
  primaryLight: '#FAF8F4',
  
  // Secondary: Rich navy blue for text and accents
  secondary: '#1A1F3A',
  secondaryHover: '#2A3455',
  secondaryLight: '#2D3550',
  
  // Accent: Elegant gold/rose gold
  accent: '#C9A96B',
  accentHover: '#B8955A',
  accentLight: '#D4B888',
  
  // Additional colors
  text: '#2C2C2C',
  textLight: '#6B6B6B',
  border: 'rgba(26, 31, 58, 0.2)',
  borderLight: 'rgba(26, 31, 58, 0.1)',
  
  // Status colors
  error: '#E8A5A5',
  success: '#A5D6A7',
  
  // Background gradients
  gradientStart: '#F5F0E8',
  gradientEnd: '#E8E0D5',
  
  // Overlay and shadows
  overlay: 'rgba(26, 31, 58, 0.8)',
  shadow: 'rgba(26, 31, 58, 0.15)',
};

// ============================================================================
// SANGEET COLOR SCHEME - FOR REFERENCE
// ============================================================================
// NOTE: This color scheme is kept for reference and future use.
// It is NOT currently active in the application (see App.tsx and cards.ts).
// These warm Bollywood-inspired colors (cream & burnt orange) will be used
// when cloning this repo for a separate Sangeet Night domain.
// ============================================================================
export const SANGEET_COLORS: ColorScheme = {
  // Primary: Warm cream/beige background
  primary: '#F8F1E9',
  primaryHover: '#F0E6D9',
  primaryLight: '#FFFBF5',
  
  // Secondary: Rich brown/burnt sienna
  secondary: '#7B4B10',
  secondaryHover: '#8F5812',
  secondaryLight: '#9A6420',
  
  // Accent: Vibrant burnt orange for Bollywood energy
  accent: '#CB6309',
  accentHover: '#E07010',
  accentLight: '#D97420',
  
  // Additional colors
  text: '#7B4B10',
  textLight: '#9A6420',
  border: 'rgba(203, 99, 9, 0.3)',
  borderLight: 'rgba(203, 99, 9, 0.15)',
  
  // Status colors
  error: '#D84315',
  success: '#7CB342',
  
  // Background gradients
  gradientStart: '#F8F1E9',
  gradientEnd: '#F0E6D9',
  
  // Overlay and shadows
  overlay: 'rgba(123, 75, 16, 0.85)',
  shadow: 'rgba(123, 75, 16, 0.2)',
};

// ============================================================================
// ACTIVE CONFIGURATION
// ============================================================================
// Default color scheme points to the currently active event (Reception)
export const COLORS = RECEPTION_COLORS;

// Color scheme map - kept for reference
// Note: Currently only RECEPTION_COLORS is used in the application
export const COLOR_SCHEMES: Record<string, ColorScheme> = {
  reception: RECEPTION_COLORS,
  sangeet: SANGEET_COLORS, // Kept for reference when cloning for separate domain
};

// Helper to get colors by card ID
// Note: Currently only returns RECEPTION_COLORS as only CARD_1 is active
export const getColorScheme = (cardId: string): ColorScheme => {
  return COLOR_SCHEMES[cardId] || RECEPTION_COLORS;
};

export type ColorKey = keyof ColorScheme;
